/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */
import React from 'react'
import clsx from 'clsx'
import {Controller, useFormContext} from 'react-hook-form'
import {useTranslation} from 'react-i18next'
import UiFormInner from 'components/Forms/UIFormInner'
import UiFormGrid from 'components/Forms/UiFormGrid'
import UiFormField from 'components/Forms/UiFormField'
import UiSelectWithGraphics, { UiSelectOption } from 'components/Ui/UiSelectWithGraphics'
import CompanyAutocomplete from 'components/CompanyAutocomplete'
import CompanyOriginSelect from 'components/Companies/forms/CompanyOriginSelect'
import CompanyFormInput from 'pages/Front/Booking/components/CompanyForm/CompanyFormInput'
import {CompanyBranchType} from 'types/enums/CompanyBranchType'
import {Company} from 'types/entities/Company'
import {LeadBasicInputs} from 'components/leads/schemas/useLeadBasicSchema'

interface LeadBasicFormAccountSectionProps {
    compact: boolean
    selectedCompany: Company | null
    companyAutocompleteKey?: number
    companyTypeOptions: UiSelectOption[]
    businessCategoryOptions: UiSelectOption[]
    highlightMissingCompany: boolean
    disableCompanyChange: boolean
    testField: (field: string) => boolean
    handleCompanySelect: (company: Company | null, shouldAutoSave?: boolean) => Promise<void>
    handleCompanyNameChange: (value: string) => void
    handleCreateCompanyFromLead: (query: string) => void
    handleAutoSaveBlur: (source: 'company') => void
}

const LeadBasicFormAccountSection: React.FC<LeadBasicFormAccountSectionProps> = ({
    compact,
    selectedCompany,
    companyAutocompleteKey = 0,
    companyTypeOptions,
    businessCategoryOptions,
    highlightMissingCompany,
    disableCompanyChange,
    testField,
    handleCompanySelect,
    handleCompanyNameChange,
    handleCreateCompanyFromLead,
    handleAutoSaveBlur,
}) => {
    const {t} = useTranslation()
    const {control, register, formState: {errors}} = useFormContext<LeadBasicInputs>()

    const companyLabel = t('Account')
    const companyPlaceholder = t('Enter account name')

    return (
        <UiFormInner title={t('Account Information')} className={clsx('LeadBasicFormAccountSection h-full lg:order-2')}>
            <UiFormGrid cols={2} gap={compact ? 3 : 3} responsive={false} className={clsx('grid-cols-2')}>
                <div className={clsx('col-span-1')}>
                    <UiFormField label={companyLabel}>
                        <div className={clsx(highlightMissingCompany && 'rounded-md ring-1 ring-red-400/60')}>
                            <CompanyAutocomplete
                                key={companyAutocompleteKey}
                                name="company_name"
                                errors={errors}
                                onSelect={handleCompanySelect}
                                onInputChange={handleCompanyNameChange}
                                onBlur={() => handleAutoSaveBlur('company')}
                                defaultValue={selectedCompany}
                                dropdownDirection="down"
                                placeholder={companyPlaceholder}
                                allowCreate={true}
                                onCreate={handleCreateCompanyFromLead}
                                disabled={disableCompanyChange}
                                showDropdownIcon={false}
                                createInfoText={t('This account will be created')}
                            />
                        </div>
                    </UiFormField>
                </div>

                <Controller
                    control={control}
                    name="company_type"
                    render={({field}) => (
                        <UiSelectWithGraphics
                            className={clsx('col-span-1')}
                            label={t('Account Type')}
                            selected={(field.value as string) || CompanyBranchType.Company}
                            setSelected={(val) => field.onChange(val || CompanyBranchType.Company)}
                            options={companyTypeOptions}
                        />
                    )}
                />

                <Controller
                    control={control}
                    name="business_category"
                    render={({field}) => (
                        <UiSelectWithGraphics
                            className={clsx('col-span-1')}
                            label={t('Branche')}
                            selected={field.value || null}
                            setSelected={(val) => field.onChange(val ?? '')}
                            options={businessCategoryOptions}
                        />
                    )}
                />

                <Controller
                    control={control}
                    name="company_origin"
                    render={({field}) => (
                        <CompanyOriginSelect
                            className={clsx('col-span-1')}
                            value={field.value || null}
                            onChange={(val) => field.onChange(val ?? '')}
                            error={errors.company_origin?.message}
                            required={testField('company_origin')}
                            errors={errors}
                        />
                    )}
                />

                <CompanyFormInput
                    className={clsx('col-span-1')}
                    label={t('Referral ID')}
                    name="referral_id"
                    register={register}
                    errors={errors}
                    placeholder={t('Enter referral ID')}
                />
            </UiFormGrid>
        </UiFormInner>
    )
}

export default LeadBasicFormAccountSection
